package org.multiplatform.kotfolio.pages.coroutines

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText

import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/coroutines/dispatchers")
@Composable
fun DispatchersPage() {
    BlogLayout(
        PageProperties(
            "Coroutines Dispatchers",
            "Dispatchers help determine the thread on which a coroutine task should be executed. (EmptyCoroutineContext, Dispatchers.Default, Dispatcher.IO, Dispatchers.Main, Dispatchers.Unconfined). Switching Between Threads Using Dispatchers and Coroutine Cooperative Cancellation."
        ),
        BlogProperties("Coroutines", "Switching Between Thread using Dispatchers | Cooperative Cancellation Coroutines")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin coroutines are a lightweight concurrency design pattern that allows you to write asynchronous, non-blocking code in a sequential, synchronous style. Dispatchers help determine the thread on which a coroutine task should be executed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutine Dispatcher is a mechanism that determines the thread or threads a coroutine will use for its execution. It acts as a scheduler for coroutines, assigning them to specific threads for their execution. The choice of dispatcher can significantly influence the behavior and performance of your coroutine-based code. (A Coroutine Dispatcher is responsible for determining the thread or threads on which the coroutine will be executed. Common dispatchers include Dispatchers.Main for UI operations and Dispatchers.IO for I/O tasks.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }


                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dispatchers are a type of ContinuationInterceptor that control where the coroutine \"cooks\" (executes) its steps. Imagine having multiple kitchens (threads) available. Dispatchers decide which kitchen is best suited for each step in the recipe.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For now, the key takeaway is that CoroutineContext helps define the overall behavior of your coroutines by providing essential elements like jobs and error handling, while dispatchers (a type of context element) control where the coroutine executes its tasks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin provides several built-in dispatchers, such as Dispatchers.Default, Dispatchers.IO, Dispatchers.Mainand Dispatchers.Unconfined, that are optimized for different types of tasks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Context Preservation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Definition: In Kotlin Flow, the upstream flow (emission and transformation operations) executes in the same coroutine context as the collector by default, ensuring seamless context management and reducing the risk of context mismanagement.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 1.em).toAttrs()) {
                    SpanText(
                        text = "Implication: Operations like map, filter, or even the emission itself inherit the context of the collect function unless overridden with flowOn.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why Context Preservation Matters",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Predictable Behavior: Ensures that all flow operations run in the same context unless explicitly altered.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 1.em).toAttrs()) {
                    SpanText(
                        text = "Thread Safety: Prevents unintentional context switches, which could lead to race conditions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 1.em).toAttrs()) {
                    SpanText(
                        text = "Performance Optimization: By default, flow avoids unnecessary overhead from context switches.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Dispatchers Works Internally",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Dispatchers like IO and Default use thread pools (pool it means a group of threads) internally. These thread pools can efficiently handle concurrent tasks by distributing work across multiple threads:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Dispatchers.IO: The number of threads is dynamically adjusted based on the number of pending I/O tasks, typically up to 64 threads.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Dispatchers.Default: Uses a shared pool of threads, typically up to the number of CPU cores on the device (e.g., 4 cores = 4 threads).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "CoroutineContext",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 1.em).toAttrs()) {
                    SpanText(
                        text = "At its core, CoroutineContext is a persistent, immutable collection of elements that define the behavior of a coroutine. Think of it as a type-safe, heterogeneous map where each element has a unique key.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Elements of the Context",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CoroutineDispatcher: Dispatchers.Main, Dispatchers.IO, Dispatchers.Default",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Job",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CoroutineName",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CoroutineExceptionHandler",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Dispatchers.Swing",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Java Swing components are not thread-safe and must be updated or accessed on the EDT. Dispatchers.Swing routes coroutine continuations directly to this thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.swing.Swing
                    import kotlinx.coroutines.withContext
                    import java.io.File
                    import javax.swing.JFileChooser
                    import javax.swing.filechooser.FileNameExtensionFilter

                    object DesktopFileDialogs {
                        suspend fun openFile(title: String, extensions: List<String> = emptyList()): File? =
                            withContext(Dispatchers.Swing) {
                                val chooser = JFileChooser().apply {
                                    dialogTitle = title
                                    fileSelectionMode = JFileChooser.FILES_ONLY
                                    isMultiSelectionEnabled = false
                                    isAcceptAllFileFilterUsed = true
                                    if (extensions.isNotEmpty()) {
                                        fileFilter = FileNameExtensionFilter(
                                            extensions.joinToString(" / ") { it.uppercase() },
                                            *extensions.toTypedArray()
                                        )
                                    }
                                    // 默认落在用户的数据目录旁边，方便找刚导出的备份
                                    currentDirectory = defaultDirectory()
                                }
                                if (chooser.showOpenDialog(null) == JFileChooser.APPROVE_OPTION) {
                                    chooser.selectedFile
                                } else null
                            }
                            
                        suspend fun saveFile(title: String, defaultName: String): File? =
                            withContext(Dispatchers.Swing) {
                                val chooser = JFileChooser().apply {
                                    dialogTitle = title
                                    fileSelectionMode = JFileChooser.FILES_ONLY
                                    isAcceptAllFileFilterUsed = true
                                    selectedFile = File(defaultDirectory(), defaultName)
                                }
                                if (chooser.showSaveDialog(null) == JFileChooser.APPROVE_OPTION) {
                                    chooser.selectedFile
                                } else null
                            }

                        private fun defaultDirectory(): File {
                            val downloads = File(System.getProperty("user.home") ?: ".", "Downloads")
                            return when {
                                downloads.isDirectory -> downloads
                                else -> File(System.getProperty("user.home") ?: ".")
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Dispatchers.Default",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Main/UI thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dispatchers.Default is a dispatcher that is optimized for CPU-intensive tasks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The default dispatcher, often referred to as the “main dispatcher” is used by default in many coroutine scopes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "Suitable for scenarios where the main thread should handle UI interactions and other non - blocking tasks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                SpanText(
                    text = "Here are some example use cases:",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Image processing",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Math calculations",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Sorting and searching",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Parsing and serializing data",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "for CPU-intensive tasks, like sorting large lists or doing complex computations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                SpanText(
                    text = "However, it’s important to note that Dispatchers.Default should not be used for blocking I/O operations, as this can cause the thread pool to become saturated and reduce the overall performance of your application.",
                    modifier = Modifier.padding(bottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    class DefaultDispatcherExample {
                        suspend fun processImage(bitmap: Bitmap): Bitmap {
                            return withContext(Dispatchers.Default) {
                                val processed = applyFilters(bitmap) 
                                val compressed = compressImage(processed) 
                                val analyzed = analyzeImage(compressed) 
                                
                                analyzed
                            }
                        }
                        
                        suspend fun parallelProcessing(data: List<Data>): List<Result> {
                            return withContext(Dispatchers.Default) {
                                data.map { item ->
                                    async {
                                        processItem(item)
                                    }
                                }.awaitAll()
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    suspend fun main(): Unit = coroutineScope {

                        launch {
                            val dispatcher = Dispatchers.Default
                                .limitedParallelism(6)
                            printCoroutinesTime(dispatcher)
                        }
                    }

                    private suspend fun printCoroutinesTime(
                        dispatcher: CoroutineDispatcher
                    ) {
                        val test = measureTimeMillis {
                            coroutineScope {
                                repeat(7) {
                                    launch(dispatcher) {
                                        Thread.sleep(1000)
                                    }
                                }
                            }
                        }
                        println(dispatcher)
                        println(test)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // Heavy computation
                    suspend fun processLargeDataset(data: List<Int>): List<Int> {
                        return withContext(Dispatchers.Default) {
                            data.map { it * it }.filter { it > 100 }.sorted()
                        }
                    }

                    // Image processing
                    suspend fun compressImage(bitmap: Bitmap): Bitmap {
                        return withContext(Dispatchers.Default) {
                            // CPU-intensive image compression
                            compressBitmap(bitmap)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Dispatcher.IO",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It uses a shared thread pool. Dispatchers.IO indicates an elastic thread pool that defaults to 64 threads or cores, whichever number is larger. It’s perfect for IO tasks, since threads in this pool is expected to spend most of the time in a non-running state. Dispatchers.IO is a dispatcher that is optimized for I/O-bound tasks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Optimized for offloading blocking I/O operations to a separate thread pool.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "Well-suited for tasks that spend a significant amount of time waiting for external resources.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "The IODispatcher is designed for handling blocking I/O operations like reading files, making network requests, or accessing databases. It has a minimum limit of 64 threads, providing plenty of bandwidth for concurrent I/O tasks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "Internally, Dispatchers.IO is backed by UnlimitedIoScheduler.limitedParallelism(...), which manages thread creation and queuing in a smart way.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                SpanText(
                    text = "Here are some example use cases:",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Network requests",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "File I/O",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Database access",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Image loading and saving",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Audio and video processing",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For input/output tasks, like reading or writing from/to a database, making network calls, or reading/writing files.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    suspend fun getImage(image: String): ByteArray = withContext(Dispatchers.IO) {
                        val images = client.get(image)
                        return@withContext images.readBytes()
                    }
                    """.trimIndent()
            )

            //https://github.com/niltsiar/kotlin_multiplatform_pokedex.git
            KotlinCode(
                code = """
                    //Arrow library
                    
                    suspend fun getDetailByUrl(url: String): Either<RepoError, PokemonDetail> =
                        withContext(Dispatchers.IO) {
                            catch({
                                val dto = apiService.getPokemonDetailByUrl(url)
                                Either.Right(dto.asDomain())
                            }) { throwable ->
                                Either.Left(throwable.toRepoError())
                            }           
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    lifecycleScope.launch(Dispatchers.IO) {
                        val response = api.fetchUsers()
                            withContext(Dispatchers.Main) {
                                updateUI(response)
                            }
                    }

                    // Database operations
                    suspend fun saveUser(user: User) {
                        withContext(Dispatchers.IO) {
                            database.insert(user)
                        }
                    }

                    // File operations
                    suspend fun readFile(fileName: String): String {
                        return withContext(Dispatchers.IO) {
                            File(fileName).readText()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class ImageLoader {
                        suspend fun loadImage(url: String): Bitmap {
                            return withContext(Dispatchers.IO) {
                                // Download from network
                                val bytes = downloadImage(url)
                                
                                cacheImage(url, bytes)
                                
                                BitmapFactory.decodeByteArray(bytes, 0, bytes.size)
                            }.also { bitmap ->
                                // (Return to Main thread)
                                withContext(Dispatchers.Main) {
                                    imageView.setImageBitmap(bitmap)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Dispatchers.IO.limitedParallelism",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dispatchers.IO.limitedParallelism is an extension of Dispatchers.IO that allows developers to limit the level of parallelism. This feature is particularly useful when you need to control the number of concurrent IO operations, ensuring that the system's resources are not exhausted by an excessive number of parallel tasks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The limitedParallelism function creates a new dispatcher that wraps around Dispatchers.IO but restricts the maximum number of parallel coroutines to a specified limit.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dispatchers.IO.limitedParallelism(n) creates a dispatcher that is limited to n threads. ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In this example, the limitedDispatcher restricts the number of concurrent IO operations to 5, even though 10 coroutines are launched. This ensures that only 5 IO operations run in parallel at any given time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    import kotlinx.coroutines.*
                    import kotlinx.coroutines.Dispatchers.IO

                    fun main() = runBlocking {
                        // Define a dispatcher with limited parallelism
                        val limitedDispatcher = IO.limitedParallelism(5)
                        
                        // Launch multiple coroutines using the limited dispatcher
                        val jobs = List(10) {
                            launch(limitedDispatcher) {
                                performBlockingIOOperation(it)
                            }
                        }
                        
                        // Wait for all coroutines to complete
                        jobs.joinAll()
                    }

                    suspend fun performBlockingIOOperation(id: Int) {
                        println(id)
                        delay(1000) // Simulate a blocking IO operation
                        println(id)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Dispatchers.Main",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dispatchers.Main is a dispatcher that is optimized for UI-related tasks. When you launch a coroutine with Dispatchers.Main, the coroutine runs on the main thread of your application, which is responsible for updating the user interface and handling user input. Dispatchers.Main without switching context is dangerous because it blocks the Android main thread, which is solely responsible for rendering the UI and handling user interactions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Running coroutines on the main thread helps ensure that UI interactions, such as button clicks or animations, are responsive and don’t result in a sluggish user interface.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "Useful for updating the UI or performing other operations that must run on the main thread in Android apps.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                SpanText(
                    text = "Here are some example use cases:",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Imagine you’re at a busy restaurant. You (the main thread) handle customer orders, but each one involves a few steps. With Dispatcher.Main, you queue tasks (like bringing water, taking orders, serving food) for the main thread one after the other. It waits for ongoing tasks to finish before starting the next task.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For UI-related tasks on the Android main thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Updating the user interface",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Handling user input and interactions",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Animations and transitions",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Displaying notifications and alerts",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "for UI-related tasks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // WHAT IT DOES: UI updates and quick operations
                    // THREAD: Main Thread (UI Thread) in Android
                    // POOL SIZE: 1 thread
                    
                    class MainDispatcherExample {
                        fun updateUI() {
                            viewModelScope.launch(Dispatchers.Main) {
                                // UI updates are safe here
                                textView.text = "Güncellendi"
                                progressBar.visibility = View.GONE
                                
                                // HOWEVER: DO NOT perform lengthy operations! 
                                // Thread.blockingOperation() // THIS WILL CAUSE ANR!
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Dispatcher.Main.immediate",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Back to our restaurant: Now, say you're in the middle of serving food but a VIP customer walks in with a quick question \uD83E\uDD2F. Instead of waiting for your current task to finish, you immediately address them. That’s Main.immediate. Jumps into action without waiting if the main thread is idle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use this when your task is quick and should execute right away if the main thread is free. Ideal for UI updates that need immediate response.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    𝘭𝘢𝘶𝘯𝘤𝘩(𝘋𝘪𝘴𝘱𝘢𝘵𝘤𝘩𝘦𝘳𝘴.𝘔𝘢𝘪𝘯.𝘪𝘮𝘮𝘦𝘥𝘪𝘢𝘵𝘦) {
                     𝘵𝘦𝘹𝘵𝘝𝘪𝘦𝘸.𝘵𝘦𝘹𝘵 = "𝘛𝘩𝘪𝘴 𝘶𝘱𝘥𝘢𝘵𝘦 𝘩𝘢𝘱𝘱𝘦𝘯𝘴 𝘪𝘯𝘴𝘵𝘢𝘯𝘵𝘭𝘺!"
                     𝘥𝘦𝘭𝘢𝘺(1000)
                     𝘢𝘯𝘰𝘵𝘩𝘦𝘳𝘝𝘪𝘦𝘸.𝘵𝘦𝘹𝘵 = "𝘗𝘳𝘰𝘤𝘦𝘴𝘴𝘦𝘥 𝘢𝘧𝘵𝘦𝘳 𝘢 𝘴𝘭𝘪𝘨𝘩𝘵 𝘥𝘦𝘭𝘢𝘺."
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Dispatchers.Unconfined",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Executes coroutines without any specific context, allowing them to resume on any available thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Suitable for cases where coroutine execution doesn’t need to be confined to a specific thread or when working with thread-local data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dispatchers.Unconfined is a dispatcher that does not impose any specific thread constraints on the coroutine. It runs coroutines on whatever thread is calling them,",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                SpanText(
                    text = "Here are some example use cases:",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Operations that need to run on a specific thread",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Operations that need to switch threads",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Operations that are not bound to any particular thread",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                SpanText(
                    text = "However, it’s important to note that Dispatchers.Unconfined should be used with caution, as it can lead to unexpected behavior if not used correctly. It is generally recommended to use one of the other built-in dispatchers, such as Dispatchers.Default , Dispatchers.IO , or Dispatchers.Main , unless you have a specific reason to use Dispatchers.Unconfined.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            SpanText(
                text = "Custom Dispatchers",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In some cases, you might need a custom dispatcher for more control over the thread pool. You can create a custom dispatcher using newSingleThreadContext() or Excutor.newFixedThreadPool().",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    private val serialScope = 
                        CoroutineScope(viewModelScope.coroutineContext + 
                                       Dispatchers.Default.limitedParallelism(1) + 
                                       SupervisorJob(viewModelScope.coroutineContext[Job]))
                    """.trimIndent()
            )

            SpanText(
                text = "newSingleThreadContext",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Crafts a coroutine execution environment using a dedicated thread with built-in yield support. It’s a delicate API that allocates native resources (the thread itself), requiring careful management.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                    val singleThreadDispatcher = newSingleThreadContext("MySingleThread")

                    CoroutineScope(singleThreadDispatcher).launch {
                        println(Thread.currentThread().name)
                    }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "newFixedThreadPoolContext",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "stablishes a coroutine execution environment with a fixed-size thread pool, enabling parallel execution of coroutines while managing thread resources carefully. You can create a dispatcher with a fixed number of threads using the Java Executors framework.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                    val fixedDispatcher = Executors.newFixedThreadPool(3).asCoroutineDispatcher()

                    CoroutineScope(fixedDispatcher).launch {
                        // Tasks will be distributed among 3 threads
                    }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "𝑙𝑎𝑢𝑛𝑐ℎ(𝐷𝑖𝑠𝑝𝑎𝑡𝑐ℎ𝑒𝑟𝑠.𝐼𝑂) vs 𝑤𝑖𝑡ℎ𝐶𝑜𝑛𝑡𝑒𝑥𝑡(𝐷𝑖𝑠𝑝𝑎𝑡𝑐ℎ𝑒𝑟𝑠.𝐼𝑂)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "𝑙𝑎𝑢𝑛𝑐ℎ(𝐷𝑖𝑠𝑝𝑎𝑡𝑐ℎ𝑒𝑟𝑠.𝐼𝑂): launch creates a 𝐧𝐞𝐰, 𝐢𝐧𝐝𝐞𝐩𝐞𝐧𝐝𝐞𝐧𝐭 coroutine and runs in 𝐩𝐚𝐫𝐚𝐥𝐥𝐞𝐥. It returns immediately.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "𝑤𝑖𝑡ℎ𝐶𝑜𝑛𝑡𝑒𝑥𝑡(𝐷𝑖𝑠𝑝𝑎𝑡𝑐ℎ𝑒𝑟𝑠.𝐼𝑂): withContext is a suspending function that 𝐖𝐀𝐈𝐓𝐒. It ensures 𝐬𝐞𝐪𝐮𝐞𝐧𝐭𝐢𝐚𝐥 execution within the current coroutine.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Switching Between Threads Using Dispatchers",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Context switching is a concept where the state of a process or thread is saved, and the execution context is switched to another process or thread. This allows multiple processes or threads to share the same CPU core or cores efficiently, enabling concurrent execution.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the operating system decides to switch from executing one task to another, it performs a context switch.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Imagine you’re fetching data in the background and need to update the UI once the data is available. Here’s how you’d switch from Dispatchers.IO to Dispatchers.Main",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "1. withContext for Thread Switching",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = " withContext changes the coroutine's context to the specified dispatcher (or any other context) and ensures that once the block is done, it returns to the original context.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The withContext function allows you to switch between dispatchers within a coroutine. It suspends the coroutine, switches to the specified dispatcher, and resumes the execution.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Switches context without launching a new coroutine.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Suspends the current coroutine until the block completes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Ensures context switching for specific tasks like I/O operations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    suspend fun fetchData() {
                        val result = withContext(Dispatchers.IO) {
                            // Perform network or database operations
                            apiService.getData()
                        }

                        // Process the result on the Main thread
                        withContext(Dispatchers.Main) {
                            updateUI(result)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "2. async-await for Concurrent Operations",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use async for concurrent operations that can be performed independently. This allows you to perform multiple tasks concurrently and await their results.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    suspend fun performConcurrentTasks() {
                        val deferredResult1 = async(Dispatchers.IO) { task1() }
                        val deferredResult2 = async(Dispatchers.IO) { task2() }

                        val result1 = deferredResult1.await()
                        val result2 = deferredResult2.await()

                        // Process results on the Main thread
                        withContext(Dispatchers.Main) {
                            updateUI(result1, result2)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                            val (topRatedMoviesResult, nowPlayingMoviesResult, popularMoviesResult) = awaitAll(
                                async { repository.getTopRatedMovies(MoviesApi.LANG_ENG, 1) },
                                async { repository.getNowPlayingMovies(MoviesApi.LANG_ENG, 1) },
                                async { repository.getPopularMovies(MoviesApi.LANG_ENG, 1) }
                            )
                        """.trimIndent()
            )

            SpanText(
                text = "3. Ensure Main Dispatcher for UI Updates",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When updating the UI after performing background tasks, switch to the Dispatchers.Main dispatcher to ensure that UI updates occur on the main thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    suspend fun fetchDataAndUpdateUI() {
                        val result = withContext(Dispatchers.IO) {
                            // Perform network or database operations
                            apiService.getData()
                        }

                        // Update UI on the Main thread
                        withContext(Dispatchers.Main) {
                            updateUI(result)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "4. Combining Dispatchers for Complex Scenarios",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For complex scenarios involving multiple dispatchers, you can combine them using the + operator. This allows you to switch between dispatchers as needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    GlobalScope.launch(Dispatchers.Default) {
                        val result = withContext(Dispatchers.IO + exceptionHandler) {
                            // Perform I/O operations with error handling
                            apiService.getData()
                        }

                        // Process the result on the Main thread
                        withContext(Dispatchers.Main) {
                            updateUI(result)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Thread.currentThread()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When using Thread.currentThread() inside a coroutine, the returned thread depends on the dispatcher that the coroutine is using:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Dispatchers.IO: The thread returned will be from the IO thread pool, designed for offloading blocking IO tasks.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Dispatchers.Main: In Android or UI-based applications, it will return the main thread (UI thread).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "While coroutines abstract thread management, this subtlety is important for managing UI responsiveness and background tasks efficiently.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Structured Concurrency",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Structured concurrency ensures that coroutines are started and managed within a specific scope, preventing memory leaks and ensuring proper cancellation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When working with asynchronous code, it’s easy to lose track of which coroutines are still running, especially if they are launched independently. This can lead to resource leaks, bugs, or unpredictable behavior. With structured concurrency, you ensure:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Coroutines have a defined lifecycle.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You can automatically cancel coroutines when their scope ends.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Child coroutines are automatically cancelled if their parent fails or is cancelled.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In structured concurrency, a coroutine scope defines the lifecycle of coroutines. Coroutines launched in a particular scope are tied to that scope’s lifecycle. When the scope is cancelled or finished, all coroutines launched in that scope are cancelled too.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class MyViewModel : ViewModel() {
                        private val viewModelJob = Job()
                        private val coroutineScope = CoroutineScope(viewModelJob + Dispatchers.Main)

                        fun fetchData() {
                            coroutineScope.launch {
                                val data = withContext(Dispatchers.IO) {
                                    // Perform network or database operation
                                }
                                // Update UI with the data
                            }
                        }

                        override fun onCleared() {
                            super.onCleared()
                            viewModelJob.cancel()
                        }
                    }
                    """.trimIndent()
            )


            SpanText(
                text = "𝘆𝗶𝗲𝗹𝗱(), 𝗶𝘀𝗔𝗰𝘁𝗶𝘃𝗲(), 𝗮𝗻𝗱 𝗲𝗻𝘀𝘂𝗿𝗲𝗔𝗰𝘁𝗶𝘃𝗲() – 𝗞𝗲𝗲𝗽𝗶𝗻𝗴 𝗖𝗼𝗿𝗼𝘂𝘁𝗶𝗻𝗲𝘀 𝗖𝗮𝗻𝗰𝗲𝗹𝗹𝗮𝘁𝗶𝗼𝗻",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "yield()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The yield function helps you control how values are produced within a sequence. Think of it as a “pause” feature: you provide a value, then temporarily stop execution until the next value is requested.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "yield() is a suspending function that hints to the dispatcher that the current coroutine can be suspended, allowing other coroutines to run (suspending function that voluntarily wait the current coroutine’s execution to allow other coroutines to run. It’s essentially a way for a coroutine to pause its execution and let other coroutines have an opportunity to be scheduled, without blocking the thread it’s running on).  After yielding, the coroutine can resume later when it’s scheduled again by the coroutine dispatcher. This helps in cooperative multitasking, where multiple coroutines can take turns executing, ensuring that long-running tasks do not monopolize the thread and other tasks can progress.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use yield() when running loops and you want to give control back to other coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Ideal when you want smooth execution without blocking the main thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Ensures the coroutine doesn’t get stuck in an infinite loop.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When used inside coroutines, yield checks for cancellations at every suspension point. This means if a coroutine is cancelled, it won’t continue running — a handy way to ensure your code remains responsive and doesn’t waste resources.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }


                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Helpful Multitasking: In multitasking, each coroutine is expected to “play nice” and occasionally yield control to allow other coroutines to execute. This is particularly important in long-running or CPU-bound coroutines that don’t naturally suspend themselves.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Non-blocking Suspension: When yield() is called, it suspends the current coroutine without blocking the underlying thread, similar to delay. However, yield immediately reschedules the coroutine to give other coroutines a chance to run.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Fair Scheduling: It’s useful when you have several coroutines working on tasks concurrently, and you want to ensure that each coroutine gets a fair chance to execute, especially in long loops or when dealing with cooperative tasks.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "yield() work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Visualize a function that generates items one at a time. Each call to yield hands over an element to be consumed, then the function “pauses” right there. When more elements are requested, it “resumes” from where it left off.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Suspends the Current Coroutine: The coroutine is paused at the point of the yield() call.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Allows Other Coroutines to Execute: The dispatcher can then switch context to other coroutines or tasks that are ready to run, enhancing the overall efficiency and responsiveness of the application.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    import kotlinx.coroutines.*

                    fun main() = runBlocking {
                        val jobA = launch {
                            repeat(5) {
                                println("Coroutine A - iteration")
                                yield() // Yield control to allow other coroutines to run
                            }
                        }

                        val jobB = launch {
                            repeat(5) {
                                println("Coroutine B - iteration")
                                yield() // Yield control to allow other coroutines to run
                            }
                        }

                        joinAll(jobA, jobB)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "isActive - Check if the Coroutine is Still Running",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "isActive is a property available inside a coroutine’s CoroutineScope. It helps you decide whether to continue execution or stop.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use isActive when you need a simple check inside your own logic.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Best for manual cancellation checks inside loops or heavy computations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Prevents unnecessary work as soon as cancellation is detected.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "ensureActive()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Unlike isActive, ensureActive() throws a CancellationException if the coroutine is canceled. This helps you exit early instead of manually checking conditions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use ensureActive() when you want an immediate stop with an exception.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Great when you want strict cancellation enforcement.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Prevents continuing a coroutine when it no longer makes sense.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li {
                    SpanText(
                        text = "Simply put, ensureActive() is a method that checks if the current coroutine is still active. It throws a CancellationException if the coroutine has been cancelled, ensuring that resources are not wasted on a coroutine that won’t be able to complete its task.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "ensureActive() is a simple extension on CoroutineScope, that throws CancellationException if current coroutine is not active. It is lightweight (what is good for performance), but does not make a space for redispatching.",
                        modifier = Modifier.padding(bottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li {
                    SpanText(
                        text = "Simply put, ensureActive() is a method that checks if the current coroutine is still active. It throws a CancellationException if the coroutine has been cancelled, ensuring that resources are not wasted on a coroutine that won’t be able to complete its task.",
                        modifier = Modifier.padding(topBottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "ensureActive() is a simple extension on CoroutineScope, that throws CancellationException if current coroutine is not active. It is lightweight (what is good for performance), but does not make a space for redispatching.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // ❌ Calculate without cooperating with the cancellation
                    suspend fun generateThumbnails(
                        images: List<File>
                    ): List<Thumbnail> {
                        val result = mutableListOf<Thumbnail>()
                        for (image in images) {
                            val bytes = image.readBytes()
                            result += createThumbnail(bytes) // CPU + posible I/O
                        }
                        return result
                    }
                    
                    suspend fun generateThumbnails(
                        images: List<File>
                    ): List<Thumbnail> = withContext(Dispatchers.Default) {
                        val result = mutableListOf<Thumbnail>()
                        for (image in images) {
                            ensureActive() // ✅ Cooperate with the cancellation
                            val bytes = withContext(Dispatchers.IO) {
                                image.readBytes()
                            }
                            result += createThumbnail(bytes)
                        }
                        result
                    }
                    
                    //ensureActive() throws CancellationException if the scope was canceled.
                    """.trimIndent()
            )

            SpanText(
                text = "Handling Background Tasks",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use WorkManager for Guaranteed Background Work",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "For tasks that need to be executed reliably, even if the app is killed or the device restarts, use WorkManager.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Leverage Coroutines for Simplicity and Performance",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Use Kotlin Coroutines for tasks that need to interact with the UI thread or for simple background operations. Coroutines provide a cleaner and more efficient way to manage asynchronous code.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Manage Lifecycle Awareness",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Ensure that background tasks are properly tied to the lifecycle of the components. Use lifecycle-aware components like lifecycleScope in Activities and Fragments to manage coroutines.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Handle Constraints Appropriately",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Specify constraints for background tasks to ensure they run under suitable conditions, such as requiring network connectivity or charging status.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use Foreground Services for User-Visible Tasks",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "For tasks that need to keep running even when the app is in the background, and require user awareness, use foreground services with notifications.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Optimize Resource Usage",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Avoid running unnecessary background tasks that can drain battery and consume data. Use appropriate intervals and conditions for periodic tasks.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Ensure Proper Error Handling",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Handle errors gracefully in background tasks to prevent crashes and ensure a smooth user experience. Use retry mechanisms where applicable.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Does every network call need Dispatchers.IO?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Not necessarily. Modern libraries like Retrofit and Ktor are non-blocking. They handle the thread-switching internally, making manual withContext(Dispatchers.IO) wrappers redundant boilerplate.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }
        }
    }
}

//https://medium.com/@abhinay212/dispatchers-in-kotlin-coroutines-ff3cf107940b